import React, { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { Check, Loader2, AlertTriangle, Copy, Gift, MessageCircle } from "lucide-react";
import { api, formatRON } from "../lib/shop";
import { useT } from "../lib/i18n";
import { toast } from "sonner";

export default function OrderConfirm() {
  const { orderId } = useParams();
  const { t } = useT();
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const isCod = params.get("cod");
  const [order, setOrder] = useState(null);
  const [payState, setPayState] = useState(isCod ? "cod" : sessionId ? "polling" : "unknown");
  const attemptsRef = useRef(0);

  useEffect(() => {
    api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
  }, [orderId]);

  useEffect(() => {
    if (!sessionId) return;
    let stopped = false;
    const poll = async () => {
      if (stopped) return;
      attemptsRef.current += 1;
      try {
        const { data } = await api.get(`/checkout/status/${sessionId}`);
        if (data.payment_status === "paid") {
          setPayState("paid");
          api.get(`/orders/${orderId}`).then((r) => setOrder(r.data)).catch(() => {});
          return;
        }
        if (data.status === "expired") { setPayState("expired"); return; }
      } catch {}
      if (attemptsRef.current >= 10) { setPayState("timeout"); return; }
      setTimeout(poll, 2000);
    };
    poll();
    return () => { stopped = true; };
  }, [sessionId, orderId]);

  const banner = () => {
    if (payState === "cod")    return { Icon: Check,         color: "text-green-600",   bg: "bg-green-50 border-green-200",   t: t("confirm.cod.t"),     d: t("confirm.cod.d") };
    if (payState === "paid")   return { Icon: Check,         color: "text-green-600",   bg: "bg-green-50 border-green-200",   t: t("confirm.paid.t"),    d: t("confirm.paid.d") };
    if (payState === "polling") return { Icon: Loader2,      color: "text-gray-700 animate-spin", bg: "bg-gray-50 border-gray-200", t: t("confirm.polling.t"), d: t("confirm.polling.d") };
    if (payState === "expired") return { Icon: AlertTriangle, color: "text-amber-600",  bg: "bg-amber-50 border-amber-200",   t: t("confirm.expired.t"), d: t("confirm.expired.d") };
    return { Icon: Check, color: "text-green-600", bg: "bg-green-50 border-green-200", t: t("confirm.unknown.t"), d: t("confirm.unknown.d") };
  };
  const B = banner();

  return (
    <div className="max-w-3xl mx-auto px-6 py-16 md:py-24" data-testid="order-confirm">
      <div className={`border ${B.bg} px-6 py-6 flex items-start gap-4`}>
        <B.Icon size={28} className={B.color} strokeWidth={1.5} />
        <div>
          <h1 className="font-display text-2xl font-black tracking-tight">{B.t}</h1>
          <p className="text-sm text-gray-600 mt-1">{B.d}</p>
        </div>
      </div>

      {order && order.generated_promo_code && (
        <div className="mt-10 border-2 border-gray-900 bg-gradient-to-br from-gray-50 to-white p-6 md:p-8" data-testid="generated-promo-card">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-gray-900 text-white flex items-center justify-center flex-shrink-0">
              <Gift size={22} strokeWidth={1.5} />
            </div>
            <div className="flex-1">
              <p className="text-xs font-bold uppercase tracking-[0.3em] text-[#188f42]">{t("confirm.gift.eye")}</p>
              <h3 className="font-display text-2xl font-black tracking-tight mt-1">{t("confirm.gift.title.a")} <span className="text-[#1ebe57]">−{order.generated_promo_code.percent}%</span></h3>
              <p className="mt-2 text-sm text-gray-600">{t("confirm.gift.d")} <span className="font-semibold">{t("confirm.gift.once")}</span>.</p>

              <div className="mt-5 flex flex-wrap items-center gap-3">
                <div className="inline-flex items-center border-2 border-gray-900 bg-white px-5 py-3 font-mono font-black text-2xl tracking-[0.2em]" data-testid="promo-code-display">
                  {order.generated_promo_code.code}
                </div>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(order.generated_promo_code.code);
                    toast.success(t("confirm.copied"));
                  }}
                  className="inline-flex items-center gap-2 border border-gray-900 px-4 py-3 text-xs font-bold uppercase tracking-wider hover:bg-gray-900 hover:text-white transition"
                  data-testid="promo-copy"
                >
                  <Copy size={14} /> {t("confirm.copy")}
                </button>
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(`${t("confirm.share.text")}: ${order.generated_promo_code.code} (${window.location.origin})`)}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 bg-[#1ebe57] text-white px-4 py-3 text-xs font-bold uppercase tracking-wider hover:bg-[#188f42] transition"
                  data-testid="promo-share-whatsapp"
                >
                  <MessageCircle size={14} /> {t("confirm.share")}
                </a>
              </div>
            </div>
          </div>
        </div>
      )}

      {order && (
        <div className="mt-10 border border-gray-200">
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center bg-gray-50">
            <div>
              <p className="text-[10px] uppercase tracking-[0.2em] text-gray-400">{t("confirm.order")}</p>
              <p className="font-mono text-sm font-semibold">#{order.id.slice(0, 8).toUpperCase()}</p>
            </div>
            <span className="text-xs uppercase tracking-wider border border-gray-300 px-3 py-1 font-mono">{order.status}</span>
          </div>
          <div className="divide-y divide-gray-200">
            {order.items.map((i, idx) => (
              <div key={idx} className="flex gap-4 px-6 py-4">
                <div className="w-16 h-16 bg-white border border-gray-200 overflow-hidden">
                  <img src={i.image} alt="" className="w-full h-full object-contain p-1" />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-sm">{i.name}</p>
                  <p className="text-xs text-gray-500 font-mono">× {i.quantity}</p>
                </div>
                <p className="font-mono text-sm">{formatRON(i.line_total)}</p>
              </div>
            ))}
          </div>
          <div className="px-6 py-5 bg-gray-50 border-t border-gray-200 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-gray-500">{t("confirm.subtotal")}</span><span className="font-mono">{formatRON(order.subtotal + (order.line_discount_total || 0))}</span></div>
            {order.line_discount_total > 0 && (
              <div className="flex justify-between text-[#188f42]" data-testid="confirm-line-discount">
                <span>{t("confirm.linediscount")}</span>
                <span className="font-mono font-semibold">−{formatRON(order.line_discount_total)}</span>
              </div>
            )}
            {order.promo_code_used && order.promo_discount_amount > 0 && (
              <div className="flex justify-between text-[#188f42]" data-testid="confirm-promo">
                <span>{t("confirm.promo")} {order.promo_code_used}</span>
                <span className="font-mono font-semibold">−{formatRON(order.promo_discount_amount)}</span>
              </div>
            )}
            {order.discount_amount > 0 && (
              <div className="flex justify-between text-[#188f42]" data-testid="confirm-discount">
                <span>{t("confirm.orderdiscount")} ({order.discount_percent}%)</span>
                <span className="font-mono font-semibold">−{formatRON(order.discount_amount)}</span>
              </div>
            )}
            <div className="flex justify-between"><span className="text-gray-500">{t("confirm.shipping")}</span><span className="font-mono">{formatRON(order.shipping_cost)}</span></div>
            <div className="flex justify-between pt-2 border-t border-gray-200 mt-2 text-base">
              <span className="font-bold uppercase">{t("confirm.total")}</span><span className="font-display font-black font-mono">{formatRON(order.total)}</span>
            </div>
          </div>
        </div>
      )}

      <div className="mt-10 flex gap-4">
        <Link to="/" className="border border-gray-900 px-6 py-3 text-sm font-semibold uppercase tracking-wider hover:bg-gray-900 hover:text-white transition">{t("confirm.back")}</Link>
        <Link to="/cont" className="text-sm font-semibold uppercase tracking-wider text-gray-600 hover:text-gray-900 px-6 py-3">{t("confirm.allorders")}</Link>
      </div>
    </div>
  );
}
